<style scoped>
.stage-dot {
	display: inline-block;
	flex: none;
	width: 0.5rem;
	height: 0.5rem;
	border-radius: 50%;
}
</style>

<template>
	<div>
		<div class="text-muted small mb-3">
			{{ props.sites.length }} sites · {{ findings }} findings
		</div>

		<div class="list-group">
			<button
				v-for="site in sortedSites"
				:key="site.id"
				type="button"
				class="list-group-item list-group-item-action d-flex align-items-start gap-2 w-100 text-start"
				:class="{ active: site.id === props.selectedSiteId }"
				@click="emit('select-site', site.id)"
			>
				<i
					:class="[CONTAINER_ICONS[site.container], 'fs-5']"
					:style="{ color: LARVAE_STAGE_COLORS[markerStage(site)] }"
					:title="site.container"
				></i>

				<div class="flex-grow-1">
					<div class="fw-semibold small">{{ site.name }}</div>
					<div class="text-muted small">
						{{ site.id }} · {{ site.container }}
					</div>
					<div class="d-flex align-items-center gap-1 mt-1">
						<span
							v-for="stage in site.stages"
							:key="stage"
							class="stage-dot"
							:style="{ background: LARVAE_STAGE_COLORS[stage] }"
							:title="stage"
						></span>
						<span class="small text-muted ms-1">
							{{ site.stages.join(", ") }}
						</span>
					</div>
				</div>

				<div class="text-end">
					<span
						class="badge rounded-pill"
						:class="
							site.id === props.selectedSiteId
								? 'bg-light text-dark'
								: 'bg-secondary'
						"
					>
						{{ site.count }}
					</span>
					<div class="small text-muted text-nowrap mt-1">
						{{ formatDates(site.findDates) }}
					</div>
				</div>
			</button>
		</div>

		<hr />

		<div class="fw-semibold small mb-2">Container types</div>
		<div
			v-for="rollup in rollups"
			:key="rollup.container"
			class="d-flex align-items-center gap-2 small text-muted"
		>
			<i :class="CONTAINER_ICONS[rollup.container]"></i>
			<span>{{ rollup.container }}</span>
			<span class="ms-auto">
				{{ rollup.sites }} sites · {{ rollup.findings }} findings
			</span>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import {
	CONTAINER_ICONS,
	LARVAE_STAGES,
	LARVAE_STAGE_COLORS,
	containerTypeRollup,
	type InvasiveAedesSite,
	type LarvaeStage,
} from "@/mock/invasive-aedes";

interface Props {
	sites: InvasiveAedesSite[];
	selectedSiteId: string | null;
}

interface Emits {
	(e: "select-site", siteId: string): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
	month: "short",
	day: "numeric",
	timeZone: "UTC",
});

const findings = computed(() =>
	props.sites.reduce((n, site) => n + site.count, 0),
);

/** Newest recorded find at a site; ISO dates sort chronologically. */
function latestFind(site: InvasiveAedesSite): string {
	const dates = [...site.findDates].sort();
	return dates[dates.length - 1] ?? "";
}

/** Sites with the most recent find first. */
const sortedSites = computed(() =>
	[...props.sites].sort((a, b) => latestFind(b).localeCompare(latestFind(a))),
);

// The icon carries the colour of the site's most advanced stage, so it matches
// the colour of that site's map marker.
function markerStage(site: InvasiveAedesSite): LarvaeStage {
	return site.stages.reduce<LarvaeStage>(
		(most, stage) =>
			LARVAE_STAGES.indexOf(stage) > LARVAE_STAGES.indexOf(most) ? stage : most,
		site.stages[0],
	);
}

/** Every find date at a site, newest first, comma-joined (`Mon D`, UTC so the
 * written day is kept: date-only ISO strings parse as UTC midnight). */
function formatDates(findDates: string[]): string {
	return [...findDates]
		.sort()
		.reverse()
		.map((iso) => DATE_FORMAT.format(new Date(iso)))
		.join(", ");
}

const rollups = containerTypeRollup();
</script>
